import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Save, Shield, BadgeCheck, AlertCircle, ExternalLink } from 'lucide-react';
import FadeIn from '../../components/FadeIn';

const AISettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [creds, setCreds] = useState({
        openai_key: '',
        gemini_key: '',
        perplexity_key: '',
        grok_key: '',
        vertex_project_id: '',
        vertex_location: '',
        vertex_credentials_json: '',
        agnes_url: '',
        manus_url: '',
        unsplash_key: '',
        use_dalle: false
    });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const docRef = doc(db, 'admin_settings', 'ai_credentials');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setCreds(prev => ({ ...prev, ...docSnap.data() }));
                }
            } catch (err) {
                console.error("Error fetching AI settings:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCreds(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setStatus({ type: '', message: '' });

        try {
            await setDoc(doc(db, 'admin_settings', 'ai_credentials'), {
                ...creds,
                updatedAt: serverTimestamp()
            }, { merge: true });
            setStatus({ type: 'success', message: 'AI Credentials saved successfully.' });
        } catch (err) {
            console.error("Error saving AI settings:", err);
            setStatus({ type: 'error', message: 'Failed to save credentials. Check permissions.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-10 text-center text-gray-400">Loading AI Configurations...</div>;

    return (
        <div className="max-w-4xl mx-auto py-8 px-4">
            <FadeIn>
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">AI Intelligence Settings</h1>
                    <p className="text-gray-400">Configure API keys and connection points for internal AI content generation.</p>
                </div>

                <div className="bg-yellow-900/20 border border-yellow-500/30 p-4 rounded-xl mb-8 flex gap-4 items-start">
                    <Shield className="text-yellow-500 shrink-0" size={24} />
                    <div>
                        <h4 className="text-yellow-500 font-bold text-sm uppercase tracking-wider mb-1">Security Notice</h4>
                        <p className="text-xs text-yellow-500/80 leading-relaxed">
                            These credentials are stored in your private Firestore instance. Ensure your Security Rules are restricted to authorized administrators. Keys are used server-side or in secure admin contexts only.
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSave} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* OpenAI */}
                        <div className="p-6 rounded-2xl border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-8 h-8 rounded bg-green-500/10 flex items-center justify-center">
                                    <span className="text-green-500 font-bold">GPT</span>
                                </div>
                                <h3 className="text-white font-bold">OpenAI (ChatGPT)</h3>
                            </div>
                            <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">API Secret Key</label>
                            <input
                                type="password"
                                name="openai_key"
                                value={creds.openai_key}
                                onChange={handleChange}
                                placeholder="sk-..."
                                className="w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all bg-[#090e18]"
                                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                            />
                            <div className="mt-2 text-[10px] text-gray-400 flex items-center gap-1">
                                <span>Get yours at:</span>
                                <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#A2CB52] hover:underline flex items-center gap-0.5">
                                    OpenAI Dashboard <ExternalLink size={10} />
                                </a>
                            </div>
                        </div>

                        {/* Google Gemini */}
                        <div className="p-6 rounded-2xl border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-8 h-8 rounded bg-blue-500/10 flex items-center justify-center">
                                    <span className="text-blue-500 font-bold">GEM</span>
                                </div>
                                <h3 className="text-white font-bold">Google Gemini</h3>
                            </div>
                            <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">API Key</label>
                            <input
                                type="password"
                                name="gemini_key"
                                value={creds.gemini_key}
                                onChange={handleChange}
                                className="w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all bg-[#090e18]"
                                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                            />
                            <div className="mt-2 text-[10px] text-gray-400 flex items-center gap-1">
                                <span>Get yours at:</span>
                                <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-[#A2CB52] hover:underline flex items-center gap-0.5">
                                    Google AI Studio <ExternalLink size={10} />
                                </a>
                            </div>
                        </div>

                        {/* Google Cloud Vertex AI */}
                        <div className="p-6 rounded-2xl border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-8 h-8 rounded bg-blue-500/10 flex items-center justify-center">
                                    <span className="text-blue-500 font-bold">VTX</span>
                                </div>
                                <h3 className="text-white font-bold">Google Vertex AI</h3>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Project ID</label>
                                    <input
                                        type="text"
                                        name="vertex_project_id"
                                        value={creds.vertex_project_id}
                                        onChange={handleChange}
                                        placeholder="e.g. my-gcp-project-123"
                                        className="w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all bg-[#090e18]"
                                        style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Location (Region)</label>
                                    <input
                                        type="text"
                                        name="vertex_location"
                                        value={creds.vertex_location}
                                        onChange={handleChange}
                                        placeholder="e.g. us-central1"
                                        className="w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all bg-[#090e18]"
                                        style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Service Account JSON</label>
                                    <textarea
                                        name="vertex_credentials_json"
                                        value={creds.vertex_credentials_json}
                                        onChange={handleChange}
                                        placeholder='{"type": "service_account", "project_id": "...", ...}'
                                        rows={4}
                                        className="w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all bg-[#090e18] text-xs font-mono"
                                        style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                                    />
                                </div>
                            </div>

                            <div className="mt-2 text-[10px] text-gray-400 flex items-center gap-1">
                                <span>Get yours at:</span>
                                <a href="https://console.cloud.google.com/vertex-ai" target="_blank" rel="noopener noreferrer" className="text-[#A2CB52] hover:underline flex items-center gap-0.5">
                                    Google Cloud Console <ExternalLink size={10} />
                                </a>
                            </div>
                        </div>

                        {/* Perplexity */}
                        <div className="p-6 rounded-2xl border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-8 h-8 rounded bg-teal-500/10 flex items-center justify-center">
                                    <span className="text-teal-500 font-bold">PPX</span>
                                </div>
                                <h3 className="text-white font-bold">Perplexity AI</h3>
                            </div>
                            <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">API Key</label>
                            <input
                                type="password"
                                name="perplexity_key"
                                value={creds.perplexity_key}
                                onChange={handleChange}
                                className="w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all bg-[#090e18]"
                                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                            />
                            <div className="mt-2 text-[10px] text-gray-500 flex items-center gap-1">
                                <span>Get yours at:</span>
                                <a href="https://www.perplexity.ai/settings/api" target="_blank" rel="noopener noreferrer" className="text-[#A2CB52] hover:underline flex items-center gap-0.5">
                                    Perplexity Settings <ExternalLink size={10} />
                                </a>
                            </div>
                        </div>

                        {/* Grok */}
                        <div className="p-6 rounded-2xl border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center">
                                    <span className="text-white font-bold">GRK</span>
                                </div>
                                <h3 className="text-white font-bold">Grok (xAI)</h3>
                            </div>
                            <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">API Key</label>
                            <input
                                type="password"
                                name="grok_key"
                                value={creds.grok_key}
                                onChange={handleChange}
                                className="w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all bg-[#090e18]"
                                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                            />
                            <div className="mt-2 text-[10px] text-gray-500 flex items-center gap-1">
                                <span>Get yours at:</span>
                                <a href="https://console.x.ai/" target="_blank" rel="noopener noreferrer" className="text-[#A2CB52] hover:underline flex items-center gap-0.5">
                                    xAI Console <ExternalLink size={10} />
                                </a>
                            </div>
                        </div>

                        {/* Agnes AI */}
                        <div className="p-6 rounded-2xl border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-8 h-8 rounded bg-purple-500/10 flex items-center justify-center">
                                    <span className="text-purple-500 font-bold">AGN</span>
                                </div>
                                <h3 className="text-white font-bold">Agnes AI</h3>
                            </div>
                            <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Integration URL / Key</label>
                            <input
                                type="text"
                                name="agnes_url"
                                value={creds.agnes_url}
                                onChange={handleChange}
                                className="w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all bg-[#090e18]"
                                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                            />
                            <div className="mt-2 text-[10px] text-gray-500">
                                <span>Use your designated integration URL provider.</span>
                            </div>
                        </div>

                        {/* Manus AI */}
                        <div className="p-6 rounded-2xl border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-8 h-8 rounded bg-orange-500/10 flex items-center justify-center">
                                    <span className="text-orange-500 font-bold text-[8px]">MNS</span>
                                </div>
                                <h3 className="text-white font-bold">Manus AI</h3>
                            </div>
                            <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Endpoint URL</label>
                            <input
                                type="text"
                                name="manus_url"
                                value={creds.manus_url}
                                onChange={handleChange}
                                className="w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all bg-[#090e18]"
                                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                            />
                            <div className="mt-2 text-[10px] text-gray-500">
                                <span>Provide the API endpoint for Manus AI integration.</span>
                            </div>
                        </div>

                        {/* Unsplash */}
                        <div className="p-6 rounded-2xl border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-8 h-8 rounded bg-gray-500/10 flex items-center justify-center">
                                    <span className="text-white font-bold text-[8px]">UNS</span>
                                </div>
                                <h3 className="text-white font-bold">Unsplash Media</h3>
                            </div>
                            <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Access Key (Regular Images)</label>
                            <input
                                type="password"
                                name="unsplash_key"
                                value={creds.unsplash_key}
                                onChange={handleChange}
                                placeholder="Access Key"
                                className="w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all bg-[#090e18]"
                                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                            />
                            <div className="mt-2 text-[10px] text-gray-400 flex items-center gap-1">
                                <span>Get yours at:</span>
                                <a href="https://unsplash.com/oauth/applications" target="_blank" rel="noopener noreferrer" className="text-[#A2CB52] hover:underline flex items-center gap-0.5">
                                    Unsplash Developer Console <ExternalLink size={10} />
                                </a>
                            </div>
                        </div>

                        {/* DALL-E Toggle */}
                        <div className="p-6 rounded-2xl border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-8 h-8 rounded bg-orange-500/10 flex items-center justify-center">
                                    <span className="text-orange-500 font-bold text-[8px]">DALI</span>
                                </div>
                                <h3 className="text-white font-bold">AI Image Generation</h3>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-400">Enable DALL-E 3 fallback generation when no relevant images are found. (Requires OpenAI Key)</span>
                                <div
                                    onClick={() => handleChange({ target: { name: 'use_dalle', type: 'checkbox', checked: !creds.use_dalle } })}
                                    className={`w-12 h-6 rounded-full transition-all cursor-pointer relative ${creds.use_dalle ? 'bg-[#A2CB52]' : 'bg-gray-700'}`}
                                >
                                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${creds.use_dalle ? 'left-7' : 'left-1'}`}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {status.message && (
                        <div className={`p-4 rounded-xl border flex items-center gap-3 animate-in slide-in-from-bottom-2 ${status.type === 'success' ? 'bg-green-900/20 border-green-500/50 text-green-200' : 'bg-red-900/20 border-red-500/50 text-red-200'}`}>
                            {status.type === 'success' ? <BadgeCheck size={18} /> : <AlertCircle size={18} />}
                            <span className="text-sm font-medium">{status.message}</span>
                        </div>
                    )}

                    <div className="flex justify-end pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex items-center px-10 py-3 rounded-xl font-bold transition-all shadow-lg hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                            style={{ backgroundColor: 'var(--accent)', color: '#000' }}
                        >
                            <Save size={18} className="mr-2" />
                            {saving ? 'Encrypting & Saving...' : 'Securely Save AI Config'}
                        </button>
                    </div>
                </form>
            </FadeIn>
        </div >
    );
};

export default AISettings;
